import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';

function AppBar({ 
  onSubmit, 
  isSimilarityPanelVisible, 
  onToggleSimilarityPanel, 
  className,
  filters,
  topics,
  onToggleFilter,
  onToggleTopicFilter,
  onCreateTopic, 
  hideInput,
  prioritySort,
  onTogglePrioritySort,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [inputValue, setInputValue] = React.useState(() => {
    const saved = localStorage.getItem('inputValue');   
    return saved || '';
  });
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  const PATH_TO_TAB = {
    '/tasks': 'tasks',
    '/feed': 'feed',
    '/': 'write',
    '/notes': 'write',
    '/essays': 'essays',
    '/journal': 'journal',
    '/profile': 'profile'
  };

  const [selectedTab, setSelectedTab] = React.useState(() => {
    return PATH_TO_TAB[location.pathname] || 'write';
  });
  
  // Update selectedTab when route changes
  React.useEffect(() => {
    setSelectedTab(PATH_TO_TAB[location.pathname] || 'write');
  }, [location.pathname]);

  // Save single input value to localStorage when it changes
  React.useEffect(() => {
    localStorage.setItem('inputValue', inputValue);
  }, [inputValue]);

  React.useEffect(() => {

  }, [location.pathname, selectedTab, inputValue]);

  const textareaRef = React.useRef(null);
  const appBarRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const dropdownMenuRef = React.useRef(null);

  React.useEffect(() => {
    if (appBarRef.current) {
      const height = appBarRef.current.offsetHeight;
      document.documentElement.style.setProperty('--app-bar-height', `${height}px`);
    }
  }, [inputValue]);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest('.dropdown-menu')
      ) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  };

  const handleChange = (e) => {
    setInputValue(e.target.value);
    adjustTextareaHeight();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      onSubmit(inputValue);
      setInputValue('');
      if (textareaRef.current) {
        textareaRef.current.style.height = '40px';
        textareaRef.current.focus();
      }
    }
  };

  // Detect if device is mobile to prevent enter key from submitting form
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  return (
    <div 
      ref={appBarRef}
      className={`flex flex-col gap-2 pt-2 pb-7 border-t border-gray-200 bg-gray-100 ${className || ''}`}
    > 
      {/* Quick navigation buttons - now outside the tasks condition */}
      <div className="flex-1 items-center overflow-x-auto px-6 my-1 " style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', '::-webkit-scrollbar': { display: 'none' } }}>
        <div className="flex items-center gap-2 w-max h-[30px]">
        <button
            onClick={() => navigate('/notes')}
            className={`hover:text-black mr-2 ${
              selectedTab === 'write' ? 'text-black font-bold' : 'text-gray-400'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
            </svg>
          </button>

          
          <button
            onClick={() => navigate('/tasks')}
            className={`hover:text-black mr-2 ${
              selectedTab === 'tasks' ? 'text-black font-bold' : 'text-gray-400'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="1" width="22" height="22" rx="6"/>
              <path d="M17 9L10 16l-4-4"/>
            </svg>
          </button>

          <button
            onClick={() => navigate('/journal')}
            className={`hover:text-black  ${
              selectedTab === 'journal' ? 'text-black font-bold' : 'text-gray-400'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </button>

          {location.pathname === '/tasks' && (
            <>
              <span className="text-gray-400">•</span>
              {/* Filters Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button 
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)} 
                  className={`text-gray-400 hover:text-black flex items-center gap-1 ${
                    (filters?.important || filters?.urgent || filters?.status?.length > 0 || filters?.topics?.length > 0)
                    ? 'text-black'
                    : ''
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                  </svg>
                </button>

                {isDropdownOpen && createPortal(
                  <div 
                    className="dropdown-menu bg-white shadow-lg rounded-lg p-3 z-[9999] flex flex-col gap-2 min-w-[200px] border border-gray-200"
                    style={{
                      position: 'fixed',
                      left: `${dropdownRef.current?.getBoundingClientRect().left}px`,
                      bottom: `${window.innerHeight - dropdownRef.current?.getBoundingClientRect().top}px`,
                    }}
                  >
                    {/* Important filter */}
                    <span
                      onClick={() => onToggleFilter('important', true)}
                      className={`cursor-pointer flex items-center ${
                        filters?.important 
                        ? 'text-black font-bold' : 'text-gray-400'
                      } hover:bg-gray-50 p-2 rounded`}
                      title="Important"
                    >
                      <span className={`text-lg mr-1 ${filters?.important ? 'text-yellow-400' : ''}`}>★</span>
                      <span>Important</span>
                    </span>

                    {/* Urgent filter */}
                    <span
                      onClick={() => onToggleFilter('urgent', true)}
                      className={`cursor-pointer flex items-center ${
                        filters?.urgent 
                        ? 'text-black font-bold' : 'text-gray-400'
                      } hover:bg-gray-50 p-2 rounded`}
                      title="Urgent"
                    >
                      <span className={`text-sm mr-1 ${filters?.urgent ? 'text-red-500' : 'grayscale'}`}>🔥</span>
                      <span>Urgent</span>
                    </span>

                    <div className="h-px bg-gray-200 my-1"></div>

                    {/* Status filters */}
                    <span
                      onClick={() => onToggleFilter('status', 'todo')}
                      className={`cursor-pointer flex items-center ${
                        filters?.status?.includes('todo')
                          ? 'text-black font-bold' 
                          : 'text-gray-400'
                      } hover:bg-gray-50 p-2 rounded`}
                    >
                      <div className="w-4 h-4 mr-2 rounded-full border-2 border-dotted border-current bg-transparent"></div>
                      <span>To Do</span>
                    </span>

                    <span
                      onClick={() => onToggleFilter('status', 'doing')}
                      className={`cursor-pointer flex items-center ${
                        filters?.status?.includes('doing')
                          ? 'text-black font-bold' 
                          : 'text-gray-400'
                      } hover:bg-gray-50 p-2 rounded`}
                    >
                      <div className="w-4 h-4 mr-2 rounded-full border border-current bg-gradient-to-r from-current from-50% to-transparent to-50%"></div>
                      <span>In Progress</span>
                    </span>

                    <span
                      onClick={() => onToggleFilter('status', 'done')}
                      className={`cursor-pointer flex items-center ${
                        filters?.status?.includes('done')
                          ? 'text-black font-bold' 
                          : 'text-gray-400'
                      } hover:bg-gray-50 p-2 rounded`}
                    >
                      <div className="w-4 h-4 mr-2 rounded-full border border-current bg-current"></div>
                      <span>Done</span>
                    </span>
                  </div>,
                  document.body
                )}
              </div>
              {/* Separator dot */}
              <span className="text-gray-400">•</span>
              {/* Topic filters */}
              {topics?.map((topic, index) => (
                <React.Fragment key={topic.id}>
                  <span
                    onClick={() => onToggleTopicFilter(topic.id)}
                    className={`cursor-pointer ${
                      filters?.topics.includes(topic.id)
                        ? 'text-black font-bold'
                        : 'text-gray-400'
                    }`}
                  >
                    {topic.name}
                  </span>
                  {index < topics.length - 1 && <span className="text-gray-400">•</span>}
                </React.Fragment>
              ))}
              <span className="text-gray-400">•</span>
              {/* Create new topic */}
              <span
                onClick={onCreateTopic}
                className="cursor-pointer text-gray-400 hover:text-gray-600"
              >
                +New
              </span>
            </>
          )}
        </div>
      </div>

      {!hideInput && (
        <form onSubmit={handleSubmit} className="flex w-full max-w-[440px] px-6 gap-2 items-end">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={handleChange}
            className="flex-1 min-h-[40px] py-2 max-h-[200px] bg-gray-100 resize-none overflow-y-auto focus:outline-none focus:ring-0"
            placeholder="Enter text..."
            rows="1"
            style={{ height: '40px' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !isMobileDevice()) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          {inputValue.trim() && (
            <button
              type="submit"
              className="h-10 w-10 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold"
            >
              →
            </button>
          )}
        </form>
      )}

      <div className="flex items-center pl-5 justify-between">
        <div className="flex items-center gap-2 w-full">
          <div className="flex rounded-full border border-gray-300 p-1 bg-white">
            <button
              onClick={() => navigate('/feed')}
              className={`h-10 w-10 flex items-center justify-center rounded-full transition-colors ${
                selectedTab === 'feed' ? 'bg-black text-white' : ''
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="16" height="9" rx="2" ry="2"/>
                <line x1="3" y1="17" x2="19" y2="17"/>
                <line x1="3" y1="22" x2="19" y2="22"/>
              </svg>
            </button>
            <button
              onClick={() => navigate('/notes')}
              className={`h-10 w-10 flex items-center justify-center rounded-full transition-colors ${
                selectedTab === 'write' ? 'bg-black text-white' : ''
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
              </svg>
            </button>
            <button
              onClick={() => navigate('/tasks')}
              className={`h-10 w-10 flex items-center justify-center rounded-full transition-colors ${
                selectedTab === 'tasks' ? 'bg-black text-white' : ''
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="1" width="22" height="22" rx="6"/>
                <path d="M17 9L10 16l-4-4"/>
              </svg>
            </button>
            <button
              onClick={() => navigate('/journal')}
              className={`h-10 w-10 flex items-center justify-center rounded-full transition-colors ${
                selectedTab === 'journal' ? 'bg-black text-white' : ''
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </button>
            <button
              onClick={() => navigate('/essays')}
              className={`h-10 w-10 flex items-center justify-center rounded-full transition-colors ${
                selectedTab === 'essays' ? 'bg-black text-white' : ''
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>

              </svg>
            </button>
            <button
              onClick={() => navigate('/profile')}
              className={`h-10 w-10 flex items-center justify-center rounded-full transition-colors ${
                selectedTab === 'profile' ? 'bg-black text-white' : ''
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </button>

          </div>
        </div>
      </div>
    </div>
  );
}

export default AppBar;